import { animate, m, useMotionValue, useMotionValueEvent, useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import { revealShare } from '@rudy/core';
import { t } from '../format';

const BASE = `${import.meta.env.BASE_URL}media/`;
const srcSet = (name: string) => `${BASE}${name}-800.webp 800w, ${BASE}${name}-1400.webp 1400w`;
const SEEN_KEY = 'rudy:reveal-seen';

function readSeen(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY) ?? 0);
  } catch {
    return 0;
  }
}
function writeSeen(day: number) {
  try {
    localStorage.setItem(SEEN_KEY, String(day));
  } catch {
    /* private mode: the reveal just doesn't replay */
  }
}

/**
 * The car fills 6.9 %–93.7 % of the photo's width (measured on the render); the wipe
 * travels over the car itself, so day 2 already shows the front end, not empty margin.
 */
const CAR_FROM = 6.9;
const CAR_TO = 93.7;
const atDay = (day: number, total: number) =>
  day <= 0 ? 0 : day >= total ? 100 : CAR_FROM + revealShare(day, total) * (CAR_TO - CAR_FROM);

/** Soft-edged wipe: photo up to `p` %, fading out over a few percent. */
const maskAt = (p: number) =>
  `linear-gradient(90deg, #000 ${Math.max(0, p - 3)}%, transparent ${Math.min(100, p + 3)}%)`;

/**
 * Hero of the Garage: the prize under one cone of garage light. The gold outline is
 * always there; the photo opens a little more every day (day / total) and is complete
 * on the last day. On the first visit of a new day the wipe moves from yesterday to
 * today, with a gold seam riding the edge.
 */
export function CarHero({
  reveal,
  alt,
}: {
  reveal: { day: number; total: number } | null;
  alt: string;
}) {
  const reduce = useReducedMotion();
  const target = reveal ? atDay(reveal.day, reveal.total) : 0;
  const [from] = useState(() => {
    if (!reveal) return 0;
    const seen = readSeen();
    if (reduce || seen >= reveal.day) return target;
    return atDay(Math.max(0, Math.min(seen || reveal.day - 1, reveal.day - 1)), reveal.total);
  });
  const p = useMotionValue(from);
  const [mask, setMask] = useState(() => maskAt(from));
  const [edge, setEdge] = useState(from);
  useMotionValueEvent(p, 'change', (v) => {
    setMask(maskAt(v));
    setEdge(v);
  });
  useEffect(() => {
    if (!reveal) return;
    writeSeen(reveal.day);
    if (from === target) return;
    const c = animate(p, target, { duration: 1.8, delay: 0.5, ease: [0.2, 0.8, 0.2, 1] });
    return () => c.stop();
  }, [from, target, p, reveal]);

  const complete = !!reveal && reveal.day >= reveal.total;

  return (
    <div className="grain relative h-[40dvh] max-h-[380px] min-h-[250px] overflow-hidden">
      {/* light cone from the ceiling */}
      <div className="absolute inset-x-0 top-0 h-full bg-[radial-gradient(60%_85%_at_50%_0%,var(--light-a),var(--light-b)_45%,transparent_70%)]" />
      <m.div
        className="absolute top-[-10%] left-1/2 h-[120%] w-[70%] -translate-x-1/2 bg-[conic-gradient(from_180deg_at_50%_0%,transparent_160deg,var(--light-c)_175deg,var(--light-d)_180deg,var(--light-c)_185deg,transparent_200deg)]"
        animate={reduce ? undefined : { opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div
        role="img"
        aria-label={
          reveal ? `${alt}. ${complete ? t('app.reveal.done') : t('app.reveal.day', reveal)}` : alt
        }
        className="absolute bottom-[12%] left-1/2 aspect-[1983/793] w-[100%] max-w-[600px] -translate-x-1/2 [mask-image:linear-gradient(to_bottom,transparent,#000_18%,#000_88%,transparent)]"
      >
        {/* outline comes from the theme (gold or silver), see --prize-outline in the CSS */}
        <div className="absolute inset-0 bg-contain bg-center bg-no-repeat [background-image:var(--prize-outline)]" />
        {reveal && (
          <img
            src={`${BASE}prize-photo-800.webp`}
            srcSet={srcSet('prize-photo')}
            sizes="(max-width: 600px) 100vw, 600px"
            alt=""
            className="absolute inset-0 size-full"
            style={{ maskImage: mask, WebkitMaskImage: mask }}
          />
        )}
        {reveal && !complete && edge > 0.5 && (
          <span
            aria-hidden
            className="absolute top-[8%] bottom-[10%] w-px bg-[linear-gradient(to_bottom,transparent,var(--color-gold-bright)_30%,var(--color-gold-bright)_70%,transparent)] shadow-[0_0_12px_2px_var(--glow)]"
            style={{ left: `${edge}%` }}
          />
        )}
      </div>

      {/* floor */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(to_top,var(--color-ink),transparent)]" />

      {reveal && (
        <div className="absolute right-4 bottom-[calc(12%+4px)] z-[2] flex items-center gap-2 text-xs text-muted">
          <span className="tabular">
            {complete ? t('app.reveal.done') : t('app.reveal.day', reveal)}
          </span>
          {!complete && (
            <span className="h-[3px] w-12 overflow-hidden rounded-full bg-line-strong">
              <span
                className="block h-full rounded-full bg-gold"
                style={{ width: `${(reveal.day / reveal.total) * 100}%` }}
              />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
